import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { ContactKind, ContactsService } from 'src/app/data/service/contacts.service';
import { OrganizationService } from 'src/app/data/service/organization.service';
import { SupplierLogoService } from 'src/app/data/service/supplier-logo.service';

/**
 * A contact's website: saved in Synced (and to a QuickBooks vendor), matched to a master contact
 * by its domain, which brings the master's logo. Saves when the field is left, as the rest of the
 * contact panel does; only a failure says anything.
 *
 *   <app-contact-website [contactId]="supplier.supplierId" kind="supplier" inputId="sc-website"
 *                        (logoChanged)="supplierLogoUrl = $event"></app-contact-website>
 */
@Component({
  selector: 'app-contact-website',
  templateUrl: './contact-website.component.html',
  styleUrls: ['./contact-website.component.scss']
})
export class ContactWebsiteComponent implements OnChanges, OnDestroy {
  @Input() contactId: string;
  @Input() kind: ContactKind = 'supplier';
  @Input() editable = true;
  @Input() inputId = 'contact-website';
  /** The master contact's logo, after a save matched one (null when none). */
  @Output() logoChanged = new EventEmitter<string | null>();

  website = '';
  private saved = '';
  busy = false;
  loaded = false;
  status: { tone: 'good' | 'warn' | 'muted' | 'error'; text: string } | null = null;
  private sub: Subscription;

  constructor(
    private contacts: ContactsService,
    private organizationService: OrganizationService,
    private supplierLogos: SupplierLogoService
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.contactId || changes.kind) { this.load(); }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  /** Where the link next to the field goes: the website as typed, made absolute. */
  get href(): string | null {
    const w = (this.saved || '').trim();
    if (!w) { return null; }
    return /^https?:\/\//i.test(w) ? w : 'https://' + w;
  }

  private load(): void {
    this.sub?.unsubscribe();
    this.website = this.saved = '';
    this.status = null;
    this.loaded = false;
    const org = this.organizationService.selectedOrganization;
    if (!org || !this.contactId) { return; }
    this.sub = this.contacts.getWebsite(org, this.kind, this.contactId).subscribe(
      w => {
        this.website = this.saved = w?.website || '';
        this.loaded = true;
      },
      () => { this.loaded = true; });
  }

  /**
   * Why a saved website brought no logo, when it didn't: a match waiting for review, a contact kind
   * not matched yet, or no logo found for the site. Silent when the logo came, or nothing changed.
   */
  private outcomeNote(w: any): { tone: 'good' | 'warn' | 'muted' | 'error'; text: string } | null {
    if (!w?.website || w?.logoUrl) { return null; }
    switch (w?.outcome) {
      case 'pending': return { tone: 'warn', text: w.message || 'Another contact has this website, so the match is waiting for review.' };
      case 'unsupported': return { tone: 'muted', text: w.message || 'Saved. No logo for this kind of contact yet.' };
      case 'linked':
      case 'created': return { tone: 'muted', text: 'Saved. No logo was found for this website yet.' };
      default: return w.message ? { tone: 'muted', text: w.message } : null;
    }
  }

  save(): void {
    const value = (this.website || '').trim();
    if (!this.editable || this.busy || value === (this.saved || '').trim()) { return; }
    const org = this.organizationService.selectedOrganization;
    if (!org || !this.contactId) { return; }

    this.busy = true;
    this.status = null;
    this.sub?.unsubscribe();
    this.sub = this.contacts.setWebsite(org, this.kind, this.contactId, value).subscribe(
      w => {
        this.busy = false;
        this.saved = w?.website || '';
        this.website = this.saved;
        this.status = this.outcomeNote(w);
        // Every badge that shows this contact looks its logo up again.
        this.supplierLogos.invalidate(org);
        this.logoChanged.emit(w?.logoUrl || null);
      },
      err => {
        this.busy = false;
        this.status = { tone: 'error', text: err?.error?.errorMessage || 'Could not save the website.' };
      });
  }
}
